import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { PresetGallery } from "../components/PresetGallery";

export const Route = createFileRoute("/viz/$slug")({
  component: VizStudio,
});

function VizStudio() {
  const { t } = useTranslation();
  const { slug } = Route.useParams();
  const viz = useQuery({ queryKey: ["viz", slug], queryFn: () => api.getViz(slug), retry: false });
  const data = viz.data;

  return (
    <div className="mx-auto max-w-[1180px] space-y-8 px-6 py-10">
      <header className="space-y-2">
        <Link to="/sessions/$slug" params={{ slug }} className="text-sm text-a2 hover:underline">← {t("common.report")}</Link>
        <h1 className="font-heading text-3xl">{t("viz.title")} · <span className="font-mono text-xl text-ink/50">{slug}</span></h1>
        <p className="text-sm text-ink/60">
          {t("viz.introBody")}{" "}
          <code className="font-mono">/research-visualize {slug} [--slides --diagrams --judge]</code>
        </p>
      </header>

      <section>
        <h2 className="font-heading text-xl">{t("viz.presetsTitle")}</h2>
        <p className="mt-1 text-sm text-ink/60">
          {t("viz.presetsBody")}
        </p>
        <PresetGallery />
      </section>

      {viz.isLoading && <p className="text-ink/50">{t("common.loading")}</p>}
      {(!data || viz.error) && (
        <div className="re-card text-sm text-ink/60">
          {t("viz.emptyBody")}{" "}
          <code className="font-mono">/research-visualize {slug}</code>
        </div>
      )}

      {data && (
        <>
          {data.charts.length > 0 && (
            <section>
              <h2 className="font-heading text-xl">{t("viz.charts", { count: data.charts.length })}</h2>
              <ul className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
                {data.charts.map((c) => (
                  <li key={c.id} className="re-card">
                    <div className="aspect-[4/3] w-full overflow-hidden rounded bg-[color:color-mix(in_oklab,var(--re-text)_3%,transparent)]">
                      <img
                        src={`/api/figures/${encodeURIComponent(slug)}/${c.png_rel.split("/").map(encodeURIComponent).join("/")}`}
                        alt={c.title}
                        className="h-full w-full object-contain"
                        loading="lazy"
                      />
                    </div>
                    <p className="mt-2 text-sm">{c.title}</p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {data.diagrams.length > 0 && (
            <section>
              <h2 className="font-heading text-xl">{t("viz.diagrams", { count: data.diagrams.length })}</h2>
              <ul className="mt-3 space-y-3 text-sm">
                {data.diagrams.map((d) => (
                  <li key={d.id} className="re-card">
                    <p className="font-medium">{d.title}</p>
                    {d.placement && <p className="text-xs text-ink/40">{d.placement}</p>}
                    <p className="mt-1 text-xs text-ink/40">{t("viz.diagramSources", { ids: d.evidence_src_ids.join(", ") })}</p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {(data.slides?.pptx || data.slides?.pdf || data.slides?.md) && (
            <section>
              <h2 className="font-heading text-xl">{t("viz.slideDeck")}</h2>
              <div className="re-card mt-3 flex flex-wrap gap-3 text-sm">
                {data.slides?.md && <span className="re-chip">slides.md</span>}
                {data.slides?.pptx && <span className="re-chip">{data.slides.pptx}</span>}
                {data.slides?.pdf && <span className="re-chip">{data.slides.pdf}</span>}
              </div>
            </section>
          )}

          {data.judge?.verdict && <JudgePanel verdict={data.judge.verdict} total={data.judge.total} passes={data.judge.passes} />}

          {data.lint && (data.lint.violations > 0 || data.lint.warnings > 0) && (
            <section>
              <h2 className="font-heading text-xl">{t("viz.lint")}</h2>
              <p className="text-sm">violations: <span className={data.lint.violations > 0 ? "text-a1" : ""}>{data.lint.violations}</span> · warnings: {data.lint.warnings}</p>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function JudgePanel({ verdict, total, passes }: { verdict: "PASS" | "FAIL"; total: number; passes: number }) {
  const okColor = verdict === "PASS" ? "var(--re-a2)" : "var(--re-a1)";
  return (
    <section>
      <h2 className="font-heading text-xl">Judge</h2>
      <div className="re-card mt-3 flex items-center gap-4">
        <div className="font-mono text-3xl" style={{ color: okColor }}>{verdict}</div>
        <div>
          <p className="text-2xl font-heading">{total}<span className="text-sm text-ink/40">/100</span></p>
          <p className="text-xs text-ink/40">{passes} pass(es)</p>
        </div>
      </div>
    </section>
  );
}
